import React, { useEffect, useRef, useState } from "react";
import {
  Box, Button, Flex, FormControl, FormLabel, HStack, IconButton,
  Input, Select, Textarea, VStack, Badge, Divider, Text, FormHelperText, Spinner,
  SimpleGrid, Code,
  useColorModeValue, useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, ArrowUpIcon, ArrowDownIcon } from "@chakra-ui/icons";
import { SUPPORTED_LANGUAGES } from "../../lib/Languages";
import { cqAPI, HOST, uploadString } from "../../lib/Auth";
import { getCampaignPlaceholders } from "./CampaignConstants";

const CHANNEL_OPTIONS = [
  { value: "phone", label: "Phone Call (Ezra)" },
  { value: "sms", label: "SMS (Text Message)" },
  // Email steps are hidden until campaigns-sender ships an email executor
  // (steps saved as email are currently skipped at execution time).
  // { value: "email", label: "Email" },
];

const UNIT_OPTIONS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

const LEAD_ADS_PLACEHOLDERS = getCampaignPlaceholders("lead-ads");

const getChannelMeta = (channel) => {
  if (channel === "phone") return { color: "blue", label: "Phone Call" };
  if (channel === "email") return { color: "purple", label: "Email" };
  return { color: "green", label: "SMS" };
};

const getTemplateDisplayName = (template, index) => (
  template?.name || template?.title || template?.label || template?.content?.name || `Template ${index + 1}`
);

const STEP_FIELD_SETTERS = Object.freeze({
  channel: (step, value) => { step.channel = value; },
  delay: (step, value) => { step.delay = value; },
  unit: (step, value) => { step.unit = value; },
  "phone_context.greeting": (step, value) => {
    step.phone_context = { ...(step.phone_context || {}), greeting: value };
  },
  "phone_context.instructions": (step, value) => {
    step.phone_context = { ...(step.phone_context || {}), instructions: value };
  },
  "phone_context.language": (step, value) => {
    step.phone_context = { ...(step.phone_context || {}), language: value };
  },
  "sms_context.template": (step, value) => {
    step.sms_context = { ...(step.sms_context || {}), template: value };
  },
  "email_context.subject": (step, value) => {
    step.email_context = { ...(step.email_context || {}), subject: value };
  },
  "email_context.template_category_id": (step, value) => {
    step.email_context = { ...(step.email_context || {}), template_category_id: value };
  },
  "email_context.template_id": (step, value) => {
    step.email_context = { ...(step.email_context || {}), template_id: value };
  },
  "email_context.template_name": (step, value) => {
    step.email_context = { ...(step.email_context || {}), template_name: value };
  },
  "email_context.html_template_url": (step, value) => {
    step.email_context = { ...(step.email_context || {}), html_template_url: value };
  },
  "email_context.body_html": (step, value) => {
    step.email_context = { ...(step.email_context || {}), body_html: value };
  },
});

const setStepField = (target, field, value) => {
  if (!Object.prototype.hasOwnProperty.call(STEP_FIELD_SETTERS, field)) return false;
  STEP_FIELD_SETTERS[field](target, value);
  return true;
};

/**
 * DripStepsEditor — visual editor for drip campaign steps.
 *
 * Each step has: channel (phone/sms), delay, unit, and channel-specific context.
 * Phone steps have: greeting, instructions, language.
 * SMS steps have: template text (with {{placeholder}} support).
 *
 * Props:
 *   steps: Array of step objects
 *   onChange: (newSteps) => void
 */
export default function DripStepsEditor({ steps = [], onChange, handleFocus, handleBlur }) {
  const stepBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const placeholderBg = useColorModeValue("white", "gray.800");
  const toast = useToast();
  const stepsRef = useRef(steps);
  const [emailCategories, setEmailCategories] = useState([]);
  const [emailTemplatesByCategory, setEmailTemplatesByCategory] = useState({});
  const [loadingTemplatesFor, setLoadingTemplatesFor] = useState(null);
  const [uploadingTemplateFor, setUploadingTemplateFor] = useState(null);

  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);

  useEffect(() => {
    let mounted = true;
    cqAPI.get('/campaigns/email-templates-categories')
      .then((response) => {
        if (mounted) setEmailCategories(response.data || []);
      })
      .catch((err) => console.error('Error fetching email template categories:', err));
    return () => { mounted = false; };
  }, []);

  const loadEmailTemplates = async (categoryId) => {
    if (!categoryId || emailTemplatesByCategory[categoryId]) return;
    setLoadingTemplatesFor(categoryId);
    try {
      const response = await cqAPI.get(`/campaigns/email-templates?id=${categoryId}`);
      setEmailTemplatesByCategory((prev) => ({ ...prev, [categoryId]: response.data || [] }));
    } catch (err) {
      console.error('Error fetching email templates:', err);
    } finally {
      setLoadingTemplatesFor(null);
    }
  };

  const updateStep = (index, field, value) => {
    const currentSteps = stepsRef.current || steps;
    const updated = currentSteps.map((s, i) => i === index ? JSON.parse(JSON.stringify(s)) : s);
    if (!updated[index]) return;
    if (!setStepField(updated[index], field, value)) return;
    stepsRef.current = updated;
    onChange(updated);
  };

  const updateStepFields = (index, fields) => {
    const currentSteps = stepsRef.current || steps;
    const updated = currentSteps.map((s, i) => i === index ? JSON.parse(JSON.stringify(s)) : s);
    if (!updated[index]) return;
    Object.entries(fields).forEach(([field, value]) => setStepField(updated[index], field, value));
    stepsRef.current = updated;
    onChange(updated);
  };

  const uploadEmailTemplate = async (html) => {
    const response = await uploadString(
      html,
      'text/html',
      `${String(Math.random() * 10000).split('.')[0]}.html`,
      false
    );
    if (response.type === 'error') throw new Error(response.msg || 'Unable to upload email template.');
    return HOST + response.url;
  };

  const handleEmailCategoryChange = async (index, categoryId) => {
    updateStepFields(index, {
      'email_context.template_category_id': categoryId,
      'email_context.template_id': '',
      'email_context.template_name': '',
      'email_context.html_template_url': '',
    });
    await loadEmailTemplates(categoryId);
  };

  const handleEmailTemplateChange = async (index, categoryId, templateId) => {
    const templates = emailTemplatesByCategory[categoryId] || [];
    const templateIndex = templates.findIndex((item) => String(item.id) === String(templateId));
    const template = templates[templateIndex];
    if (!template) return;
    setUploadingTemplateFor(index);
    try {
      const htmlTemplateUrl = await uploadEmailTemplate(template.html || '');
      updateStepFields(index, {
        'email_context.template_id': template.id,
        'email_context.template_name': getTemplateDisplayName(template, templateIndex),
        'email_context.html_template_url': htmlTemplateUrl,
        'email_context.body_html': '',
      });
    } catch (err) {
      console.error('Error uploading selected email template:', err);
      toast({
        title: "Template Upload Failed",
        description: err.message || "Unable to save the selected email template.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingTemplateFor(null);
    }
  };

  const addStep = () => {
    const currentSteps = stepsRef.current || steps;
    const updated = [
      ...currentSteps,
      {
        delay: 30,
        unit: "minutes",
        channel: "phone",
        phone_context: { greeting: "", instructions: "" },
        sms_context: { template: "" },
        email_context: { subject: "", body_html: "" },
      },
    ];
    stepsRef.current = updated;
    onChange(updated);
  };

  const removeStep = (index) => {
    const currentSteps = stepsRef.current || steps;
    const updated = currentSteps.filter((_, i) => i !== index);
    stepsRef.current = updated;
    onChange(updated);
  };

  const moveStep = (index, direction) => {
    const newIndex = index + direction;
    const currentSteps = stepsRef.current || steps;
    if (newIndex < 0 || newIndex >= currentSteps.length) return;
    const updated = [...currentSteps];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    stepsRef.current = updated;
    onChange(updated);
    handleBlur?.(); // Close placeholder overlay — templatePath index would be stale
  };

  return (
    <VStack spacing={4} align="stretch" w="100%">
      {steps.map((step, index) => {
                const channelMeta = getChannelMeta(step.channel);
                const selectedCategoryId = step.email_context?.template_category_id || "";
                const emailTemplates = emailTemplatesByCategory[selectedCategoryId] || [];
                const selectedTemplateMissing = !!step.email_context?.template_id
                  && !emailTemplates.some((template) => String(template.id) === String(step.email_context.template_id));
                return (
        <Box
          key={index}
          p={4}
          bg={stepBg}
          borderRadius="md"
          border="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Badge colorScheme={channelMeta.color} fontSize="sm">
                Step {index + 1}
              </Badge>
              <Badge variant="outline">{channelMeta.label}</Badge>
            </HStack>
            <HStack spacing={1}>
              <IconButton
                icon={<ArrowUpIcon />} size="xs" variant="ghost"
                onClick={() => moveStep(index, -1)}
                isDisabled={index === 0}
                aria-label="Move up"
              />
              <IconButton
                icon={<ArrowDownIcon />} size="xs" variant="ghost"
                onClick={() => moveStep(index, 1)}
                isDisabled={index === steps.length - 1}
                aria-label="Move down"
              />
              <IconButton
                icon={<DeleteIcon />} size="xs" variant="ghost" colorScheme="red"
                onClick={() => removeStep(index)}
                aria-label="Remove step"
              />
            </HStack>
          </Flex>

          <HStack spacing={4} mb={3}>
            <FormControl w="160px">
              <FormLabel fontSize="sm">Channel</FormLabel>
              <Select
                size="sm"
                value={step.channel || "phone"}
                onChange={(e) => updateStep(index, "channel", e.target.value)}
              >
                {CHANNEL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl w="100px">
              <FormLabel fontSize="sm">Delay</FormLabel>
              <Input
                size="sm"
                type="number"
                value={step.delay || 0}
                onChange={(e) => updateStep(index, "delay", parseInt(e.target.value) || 0)}
              />
            </FormControl>

            <FormControl w="130px">
              <FormLabel fontSize="sm">Unit</FormLabel>
              <Select
                size="sm"
                value={step.unit || "minutes"}
                onChange={(e) => updateStep(index, "unit", e.target.value)}
              >
                {UNIT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </FormControl>
          </HStack>

          <Divider mb={3} />

          {step.channel === "phone" && (
            <VStack spacing={2} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Greeting</FormLabel>
                <Textarea
                  size="sm" rows={2}
                  placeholder="Hi {{.PatientFirstname}}, this is {{.OfficeName}} calling about {{.AdService}}..."
                  value={step.phone_context?.greeting || ""}
                  onChange={(e) => updateStep(index, "phone_context.greeting", e.target.value)}
                  onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "phone_context", "greeting"])}
                  onBlur={handleBlur}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Extra Instructions for AI</FormLabel>
                <Textarea
                  size="sm" rows={2}
                  placeholder="If voicemail, leave a brief message with callback number."
                  value={step.phone_context?.instructions || ""}
                  onChange={(e) => updateStep(index, "phone_context.instructions", e.target.value)}
                  onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "phone_context", "instructions"])}
                  onBlur={handleBlur}
                />
              </FormControl>
              <FormControl w={{ base: "100%", md: "220px" }}>
                <FormLabel fontSize="sm">Language</FormLabel>
                <Select
                  size="sm"
                  value={step.phone_context?.language || ""}
                  onChange={(e) => updateStep(index, "phone_context.language", e.target.value)}
                >
                  <option value="">Auto-detect</option>
                  {SUPPORTED_LANGUAGES.map((language) => (
                    <option key={language.value} value={language.value}>
                      {language.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          )}

          {step.channel === "sms" && (
            <FormControl>
              <FormLabel fontSize="sm">SMS Template</FormLabel>
              <Textarea
                size="sm" rows={3}
                placeholder="Hi {{.PatientFirstname}}, we tried reaching you about {{.AdService}} at {{.OfficeName}}. Reply YES to schedule or call us at {{.OfficePhone}}."
                value={step.sms_context?.template || ""}
                onChange={(e) => updateStep(index, "sms_context.template", e.target.value)}
                onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "sms_context", "template"])}
                onBlur={handleBlur}
              />
            </FormControl>
          )}

          {step.channel === "email" && (
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Subject</FormLabel>
                <Input
                  size="sm"
                  placeholder="Your {{.AdService}} request at {{.OfficeName}}"
                  value={step.email_context?.subject || ""}
                  onChange={(e) => updateStep(index, "email_context.subject", e.target.value)}
                  onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "email_context", "subject"])}
                  onBlur={handleBlur}
                />
              </FormControl>

              <HStack spacing={3} align="flex-start">
                <FormControl>
                  <FormLabel fontSize="sm">Template Category</FormLabel>
                  <Select
                    size="sm"
                    placeholder="Select category"
                    value={selectedCategoryId}
                    onChange={(e) => handleEmailCategoryChange(index, e.target.value)}
                  >
                    {emailCategories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isDisabled={!selectedCategoryId || loadingTemplatesFor === selectedCategoryId}>
                  <FormLabel fontSize="sm">Email Template</FormLabel>
                  <Select
                    size="sm"
                    placeholder={loadingTemplatesFor === selectedCategoryId ? "Loading..." : "Select template"}
                    value={step.email_context?.template_id || ""}
                    onFocus={() => loadEmailTemplates(selectedCategoryId)}
                    onChange={(e) => handleEmailTemplateChange(index, selectedCategoryId, e.target.value)}
                  >
                    {selectedTemplateMissing && (
                      <option value={step.email_context.template_id}>
                        {step.email_context.template_name || "Saved template"}
                      </option>
                    )}
                    {emailTemplates.map((template, templateIndex) => (
                      <option key={template.id} value={template.id}>{getTemplateDisplayName(template, templateIndex)}</option>
                    ))}
                  </Select>
                  {uploadingTemplateFor === index && (
                    <HStack mt={1} spacing={1}><Spinner size="xs" /><Text fontSize="xs">Saving template...</Text></HStack>
                  )}
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">Email Body</FormLabel>
                <Textarea
                  size="sm" rows={4}
                  placeholder="Hi {{.PatientFirstname}}, thanks for your interest in {{.AdService}} at {{.OfficeName}}."
                  value={step.email_context?.body_html || ""}
                  onChange={(e) => updateStep(index, "email_context.body_html", e.target.value)}
                  onFocus={() => handleFocus?.(["settings", "drip", "steps", index, "email_context", "body_html"])}
                  onBlur={handleBlur}
                />
                <FormHelperText>
                  Use either a selected template or a body. If both are present, the body is used.
                </FormHelperText>
                {step.email_context?.html_template_url && !step.email_context?.body_html && (
                  <Text fontSize="xs" color="gray.500" mt={1}>Template saved for this step.</Text>
                )}
              </FormControl>

              {step.email_context?.html_template_url && (
                <Box p={3} borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={placeholderBg}>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>Supported Placeholders</Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {LEAD_ADS_PLACEHOLDERS.map((placeholder) => (
                      <Box key={placeholder.placeholder}>
                        <Code fontSize="xs">{placeholder.placeholder}</Code>
                        <Text fontSize="xs" color="gray.500">{placeholder.meaning}</Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </VStack>
          )}
        </Box>
        );
      })}

      <Button leftIcon={<AddIcon />} size="sm" variant="outline" onClick={addStep}>
        Add Step
      </Button>
    </VStack>
  );
}
